import React from 'react'
import { ActionIcon, Button, Group, NumberInput, Select, Stack, TextInput } from '@mantine/core'
import { XIcon } from '@phosphor-icons/react'

export type FilterOperator =
  | 'contains'
  | 'not_contains'
  | 'equals'
  | 'not_equals'
  | 'starts_with'
  | 'ends_with'
  | 'empty'
  | 'not_empty'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'

export type FilterDataType = 'string' | 'number'
export type FilterCombinator = 'AND' | 'OR'

export interface FilterCondition {
  operator: FilterOperator
  value: string
  value2?: string
}

export interface Filter {
  conditions: FilterCondition[]
  combinator: FilterCombinator
}

const MAX_CONDITIONS = 2

const DEFAULT_CONDITION_BY_TYPE: Record<FilterDataType, FilterCondition> = {
  string: { operator: 'contains', value: '' },
  number: { operator: 'equals', value: '' },
}

export const DEFAULT_FILTER_BY_TYPE: Record<FilterDataType, Filter> = {
  string: { conditions: [DEFAULT_CONDITION_BY_TYPE.string], combinator: 'AND' },
  number: { conditions: [DEFAULT_CONDITION_BY_TYPE.number], combinator: 'AND' },
}

const STRING_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'contains', label: '含む' },
  { value: 'not_contains', label: '含まない' },
  { value: 'equals', label: '等しい' },
  { value: 'not_equals', label: '等しくない' },
  { value: 'starts_with', label: 'で始まる' },
  { value: 'ends_with', label: 'で終わる' },
  { value: 'empty', label: '空' },
  { value: 'not_empty', label: '空でない' },
]

const NUMBER_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'equals', label: '等しい' },
  { value: 'not_equals', label: '等しくない' },
  { value: 'gt', label: 'より大きい' },
  { value: 'gte', label: '以上' },
  { value: 'lt', label: 'より小さい' },
  { value: 'lte', label: '以下' },
  { value: 'between', label: '範囲' },
  { value: 'empty', label: '空' },
  { value: 'not_empty', label: '空でない' },
]

const COMBINATOR_OPTIONS: { value: FilterCombinator; label: string }[] = [
  { value: 'AND', label: 'かつ (AND)' },
  { value: 'OR', label: 'または (OR)' },
]

const NO_VALUE_OPERATORS: FilterOperator[] = ['empty', 'not_empty']
const TWO_VALUE_OPERATORS: FilterOperator[] = ['between']

// 条件が有効か（入力値ありか値不要オペレータか）判定
const isConditionActive = (c: FilterCondition): boolean => {
  if (NO_VALUE_OPERATORS.includes(c.operator)) return true
  if (TWO_VALUE_OPERATORS.includes(c.operator)) {
    return (c.value?.length ?? 0) > 0 || (c.value2?.length ?? 0) > 0
  }
  return c.value.length > 0
}

// フィルタ全体に有効条件があるか判定
export const isFilterActive = (filter: Filter | undefined): boolean => {
  if (!filter) return false
  return filter.conditions.some(isConditionActive)
}

// セル値が空（null/undefined/空文字）か判定
const isCellEmpty = (cellValue: unknown): boolean =>
  cellValue === null || cellValue === undefined || cellValue === ''

// 文字列セルに対する1条件の判定
const applyConditionString = (cell: string, c: FilterCondition): boolean => {
  const q = c.value
  switch (c.operator) {
    case 'contains':
      return cell.toLowerCase().includes(q.toLowerCase())
    case 'not_contains':
      return !cell.toLowerCase().includes(q.toLowerCase())
    case 'equals':
      return cell === q
    case 'not_equals':
      return cell !== q
    case 'starts_with':
      return cell.toLowerCase().startsWith(q.toLowerCase())
    case 'ends_with':
      return cell.toLowerCase().endsWith(q.toLowerCase())
    case 'empty':
      return cell === ''
    case 'not_empty':
      return cell !== ''
    default:
      return true
  }
}

// 数値セルに対する1条件の判定
const applyConditionNumber = (cellValue: unknown, c: FilterCondition): boolean => {
  if (c.operator === 'empty') return isCellEmpty(cellValue)
  if (c.operator === 'not_empty') return !isCellEmpty(cellValue)

  if (isCellEmpty(cellValue)) return false
  const cellNum = typeof cellValue === 'number' ? cellValue : Number(cellValue)
  if (!Number.isFinite(cellNum)) return false

  const v1 = c.value === '' ? NaN : Number(c.value)

  switch (c.operator) {
    case 'equals':
      return Number.isFinite(v1) && cellNum === v1
    case 'not_equals':
      return Number.isFinite(v1) && cellNum !== v1
    case 'gt':
      return Number.isFinite(v1) && cellNum > v1
    case 'gte':
      return Number.isFinite(v1) && cellNum >= v1
    case 'lt':
      return Number.isFinite(v1) && cellNum < v1
    case 'lte':
      return Number.isFinite(v1) && cellNum <= v1
    case 'between': {
      const v2Raw = c.value2 ?? ''
      const v2 = v2Raw === '' ? NaN : Number(v2Raw)
      if (!Number.isFinite(v1) && !Number.isFinite(v2)) return true
      if (Number.isFinite(v1) && cellNum < v1) return false
      if (Number.isFinite(v2) && cellNum > v2) return false
      return true
    }
    default:
      return true
  }
}

// データ型に応じて1条件を適用
const applyCondition = (
  cellValue: unknown,
  c: FilterCondition,
  dataType: FilterDataType,
): boolean => {
  if (dataType === 'number') return applyConditionNumber(cellValue, c)
  const cell = isCellEmpty(cellValue) ? '' : String(cellValue)
  return applyConditionString(cell, c)
}

// 全条件を AND/OR で結合してセルに適用
export const applyColumnFilter = (
  cellValue: unknown,
  filter: Filter,
  dataType: FilterDataType = 'string',
): boolean => {
  const active = filter.conditions.filter(isConditionActive)
  if (active.length === 0) return true
  if (filter.combinator === 'OR') {
    return active.some((c) => applyCondition(cellValue, c, dataType))
  }
  return active.every((c) => applyCondition(cellValue, c, dataType))
}

interface ConditionRowProps {
  condition: FilterCondition
  dataType: FilterDataType
  onChange: (c: FilterCondition) => void
  onRemove?: () => void
}

// データ型ごとのデフォルトオペレータ
const FALLBACK_OPERATOR_BY_TYPE: Record<FilterDataType, FilterOperator> = {
  string: 'contains',
  number: 'equals',
}

// 1条件の入力UI（オペレータ + 値入力）
const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  dataType,
  onChange,
  onRemove,
}) => {
  const operators = dataType === 'number' ? NUMBER_OPERATORS : STRING_OPERATORS
  const needsValue = !NO_VALUE_OPERATORS.includes(condition.operator)
  const needsTwoValues = TWO_VALUE_OPERATORS.includes(condition.operator)

  // オペレータ変更時、不要になったvalue2を破棄
  const handleOperatorChange = (v: string | null) => {
    const op = (v as FilterOperator) ?? FALLBACK_OPERATOR_BY_TYPE[dataType]
    if (TWO_VALUE_OPERATORS.includes(op)) {
      onChange({ ...condition, operator: op })
    } else {
      const { value2: _drop, ...rest } = condition
      onChange({ ...rest, operator: op })
    }
  }

  return (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <Select
        value={condition.operator}
        data={operators}
        onChange={handleOperatorChange}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
        checkIconPosition='right'
        style={{ flex: '0 0 130px' }}
      />
      {needsValue && dataType === 'number' && (
        <NumberInput
          value={condition.value === '' ? '' : Number(condition.value)}
          onChange={(v) =>
            onChange({ ...condition, value: v === '' || v === null ? '' : String(v) })
          }
          placeholder={needsTwoValues ? 'から' : '値を入力...'}
          hideControls
          allowDecimal
          style={{ flex: 1 }}
        />
      )}
      {needsValue && dataType === 'string' && (
        <TextInput
          value={condition.value}
          onChange={(e) => onChange({ ...condition, value: e.currentTarget.value })}
          placeholder="値を入力..."
          style={{ flex: 1 }}
        />
      )}
      {needsTwoValues && dataType === 'number' && (
        <NumberInput
          value={condition.value2 === undefined || condition.value2 === '' ? '' : Number(condition.value2)}
          onChange={(v) =>
            onChange({ ...condition, value2: v === '' || v === null ? '' : String(v) })
          }
          placeholder="まで"
          hideControls
          allowDecimal
          style={{ flex: 1 }}
        />
      )}
      {onRemove && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onRemove}
          aria-label="条件を削除"
        >
          <XIcon size={14} />
        </ActionIcon>
      )}
    </Group>
  )
}

interface Props {
  filter: Filter
  onChange: (filter: Filter) => void
  dataType?: FilterDataType
}

// カラムごとのフィルタUI（最大2条件 + AND/OR切替）
export const ColumnFilter: React.FC<Props> = ({ filter, onChange, dataType = 'string' }) => {
  // 指定インデックスの条件を更新
  const updateCondition = (idx: number, c: FilterCondition) => {
    const conditions = filter.conditions.map((curr, i) => (i === idx ? c : curr))
    onChange({ ...filter, conditions })
  }

  // 条件追加（上限到達なら無視）
  const addCondition = () => {
    if (filter.conditions.length >= MAX_CONDITIONS) return
    onChange({
      ...filter,
      conditions: [...filter.conditions, DEFAULT_CONDITION_BY_TYPE[dataType]],
    })
  }

  // 条件削除（0件なら既定値を残す）
  const removeCondition = (idx: number) => {
    const next = filter.conditions.filter((_, i) => i !== idx)
    onChange({
      ...filter,
      conditions: next.length === 0 ? [DEFAULT_CONDITION_BY_TYPE[dataType]] : next,
    })
  }

  // AND/OR 切替
  const handleCombinatorChange = (v: string | null) => {
    onChange({ ...filter, combinator: (v as FilterCombinator) ?? 'AND' })
  }

  return (
    <Stack gap={8}>
      <ConditionRow
        condition={filter.conditions[0]}
        dataType={dataType}
        onChange={(c) => updateCondition(0, c)}
        onRemove={() => removeCondition(0)}
      />
      {filter.conditions.length >= 2 ? (
        <>
          <Select
            value={filter.combinator}
            data={COMBINATOR_OPTIONS}
            onChange={handleCombinatorChange}
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
            checkIconPosition='right'
            w={140}
          />
          <ConditionRow
            condition={filter.conditions[1]}
            dataType={dataType}
            onChange={(c) => updateCondition(1, c)}
            onRemove={() => removeCondition(1)}
          />
        </>
      ) : (
        <Group justify="flex-start">
          <Button
            size="xs"
            variant="subtle"
            onClick={addCondition}
          >
            + 条件追加
          </Button>
        </Group>
      )}
    </Stack>
  )
}
